import { Injectable } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Order } from '@usf/ngrx-order';
import { OrderDownloadDtoService } from './order-download-dto.service';
import { Message } from '@app/ngrx-message/models/client/message';
import { TranslateService } from '@ngx-translate/core';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { FileHandlerBaseService } from '../file-handler/file-handler-base.service';
import { ToastService } from '../toast/toast.service';
import { Platform } from '@ionic/angular';
import { DatePipe } from '@angular/common';
import { SentenceCasePipe } from '@shared/pipes/sentence-case.pipe';
import { LoadingSpinnerService } from '../loading-spinner/loading-spinner.service';
import { DownloadOrdersOptions } from '@order/models/download-orders-event';
import { SeperatedColumn } from '@shared/models/seperated-columns';
import {
  CONTENT_TYPE_CSV,
  CONTENT_TYPE_CSV_ANDROID,
} from '@shared/constants/files';

@Injectable({
  providedIn: 'root',
})
export class OrderDownloadService extends FileHandlerBaseService {
  constructor(
    private translate: TranslateService,
    private messageStoreService: MessageStoreService,
    toastService: ToastService,
    public translateService: TranslateService,
    public platform: Platform,
    public datePipe: DatePipe,
    public sentenceCasePipe: SentenceCasePipe,
    public loadingSpinnerService: LoadingSpinnerService,
  ) {
    super(
      toastService,
      translateService,
      platform,
      datePipe,
      sentenceCasePipe,
      loadingSpinnerService,
    );
  }

  public downloadOrder(orders: Order[], options: DownloadOrdersOptions) {
    let fileContents: string;

    const orderHeader = orders[0].orderHeader;
    const orderItems = orders[0].orderItems;

    const {
      fileType,
      fileName,
      products,
      multipleExports,
      failToast,
    } = options;

    let contentType = fileType.contentType;

    switch (fileType?.fileExtension.toUpperCase()) {
      case 'CSV':
        fileContents = this.handleCSVDownload(orders, options);
        contentType = this.platform.is('android')
          ? CONTENT_TYPE_CSV_ANDROID
          : CONTENT_TYPE_CSV;
        break;
      case 'CMP':
        if (multipleExports) {
          fileContents = OrderDownloadDtoService.formatMultiOrdersToCMP(
            orders,
            products,
          );
        } else {
          fileContents = OrderDownloadDtoService.formatOrderToCMP(
            orderHeader,
            orderItems,
            products,
          );
        }
        break;
      case 'STD':
        if (multipleExports) {
          fileContents = OrderDownloadDtoService.formatMultiOrdersToCMP(
            orders,
            products,
          );
        } else {
          fileContents = OrderDownloadDtoService.formatOrderToCMP(
            orderHeader,
            orderItems,
            products,
          );
        }
        break;
      case 'STD2':
        if (multipleExports) {
          fileContents = OrderDownloadDtoService.formatMultiOrdersToSTD2(
            orders,
            products,
          );
        } else {
          fileContents = OrderDownloadDtoService.formatOrderToSTD2(
            orderHeader,
            orderItems,
            products,
          );
        }
        break;
      case 'XML':
        fileContents = OrderDownloadDtoService.formatOrdersToXML(
          orders,
          products,
        );
        break;
      default:
        break;
    }
    if (!fileContents || fileContents.length === 0) {
      this.openToast(
        failToast || 'i18n.orderPage.orderDownloadFail',
        MessageTypeEnum.failure,
      );
      return;
    }
    const blob: Blob = new Blob([fileContents], {
      type: fileType.contentType,
    });

    return this.downloadFile(
      blob,
      fileName,
      contentType,
      this.platform.is('android'),
    );
  }

  handleCSVDownload(orders: Order[], options: DownloadOrdersOptions): string {
    const { fileType, products, isCart, myOrdersFormat, customers } = options;

    let seperatedColumns: SeperatedColumn[];
    let numberOfRows: number;

    if (myOrdersFormat) {
      numberOfRows = orders.length;

      seperatedColumns = OrderDownloadDtoService.createSeperatedColumnsForMyOrders(
        orders,
        customers,
      );
    } else {
      numberOfRows = orders.reduce(
        (partialSum, order) => partialSum + (order.orderItems?.length || 0),
        0,
      );
      if (isCart) {
        seperatedColumns = OrderDownloadDtoService.createSeperatedColumnsForCart(
          products,
          orders[0],
        );
      } else {
        seperatedColumns = OrderDownloadDtoService.createMultipleSeperatedColumnsForSubmitted(
          products,
          orders,
        );
      }
    }

    // Create the separated values as string
    return OrderDownloadDtoService.stringifySeparatedValues(
      seperatedColumns,
      numberOfRows,
      fileType.seperator,
    );
  }

  public async downloadAndOpenOrder(
    orders: Order[],
    options: DownloadOrdersOptions,
  ) {
    try {
      await this.downloadOrder(orders, options);
      this.openToast(
        options?.successToast || 'i18n.orderPage.orderDownloadSuccess',
        MessageTypeEnum.success,
      );
    } catch (error) {
      console.log(error);
      this.showErrorToast(
        options?.failToast || 'i18n.orderPage.orderDownloadFail',
      );
    }
  }

  openToast(message: string, messageType: MessageTypeEnum) {
    const toast = {
      stack: null,
      watermark: new Date().toUTCString(),
      read: false,
      type: messageType,
      display: this.translate.instant(message),
      toast: true,
      sticky: false,
    } as Message;
    this.messageStoreService.upsertMessage(toast);
  }
}
